
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { LoadingFallback } from '@/components/common/LoadingFallback';
import { toast } from 'sonner';
import { getLogger } from '@/utils/logger';

const log = getLogger({ module: 'AuthCallback' });

const AuthCallback = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isProcessing, setIsProcessing] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const handleAuthCallback = async () => {
      try {
        log.info('AuthCallback - Processing authentication callback');
        
        // Check URL parameters for auth type and errors
        const type = searchParams.get('type');
        const error_code = searchParams.get('error_code');
        const error_description = searchParams.get('error_description');

        log.info('AuthCallback - URL params:', { 
          type, 
          error_code,
          error_description
        });

        // Handle errors from the callback URL
        if (error_code || error_description) {
          let errorMsg = error_description || `Authentication error: ${error_code}`;
          
          // Provide user-friendly message for expired links
          if (error_code === 'otp_expired') {
            errorMsg = 'This password reset link has expired. Please request a new one.';
          }
          
          log.error('AuthCallback - URL error:', errorMsg);
          setError(errorMsg);
          toast.error(errorMsg);
          setIsProcessing(false);
          return;
        }

        // For auth callbacks with tokens in URL hash, Supabase processes them asynchronously
        // We need to wait for the session to be ready
        let sessionCheckAttempts = 0;
        const maxAttempts = 20; // 10 seconds total (500ms * 20)
        
        const waitForSession = async (): Promise<boolean> => {
          while (sessionCheckAttempts < maxAttempts) {
            const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
            
            if (sessionError) {
              log.error('AuthCallback - Session error:', sessionError);
              return false;
            }
            
            if (sessionData.session) {
              log.info('AuthCallback - Session found after', sessionCheckAttempts, 'attempts');
              return true;
            }
            
            // Wait 500ms before next attempt
            await new Promise(resolve => setTimeout(resolve, 500));
            sessionCheckAttempts++;
          }
          
          return false;
        };

        // Handle different authentication types
        if (type === 'recovery') {
          log.info('AuthCallback - Password recovery flow, waiting for session');
          const hasSession = await waitForSession();
          
          if (hasSession) {
            toast.success('Password recovery link verified. You can now set a new password.');
            navigate('/auth?view=reset-password', { replace: true });
          } else {
            setError('Invalid or expired recovery link. Please request a new password reset.');
            toast.error('Recovery link invalid');
          }
        } else if (type === 'signup') {
          log.info('AuthCallback - Email confirmation flow, waiting for session');
          const hasSession = await waitForSession();
          
          if (hasSession) {
            toast.success('Email confirmed successfully!');
            navigate('/', { replace: true });
          } else {
            setError('Email confirmation failed. Please try again.');
            toast.error('Email confirmation failed');
          }
        } else if (type === 'invite') {
          log.info('AuthCallback - Invite flow, waiting for session');
          const hasSession = await waitForSession();
          
          if (hasSession) {
            toast.success('Invite accepted! Welcome to FOMO.');
            navigate('/', { replace: true });
          } else {
            setError('Invalid invite link. Please request a new invitation.');
            toast.error('Invalid invite link');
          }
        } else {
          // Default case - check if we have a valid session immediately
          const { data: sessionData } = await supabase.auth.getSession();
          
          if (sessionData.session) {
            log.info('AuthCallback - Valid session found, redirecting to app');
            toast.success('Successfully authenticated!');
            navigate('/', { replace: true });
          } else {
            log.info('AuthCallback - No valid session, redirecting to auth');
            navigate('/auth', { replace: true });
          }
        }
      } catch (err) {
        log.error('AuthCallback - Unexpected error:', err);
        setError('An unexpected error occurred. Please try again.');
        toast.error('Authentication failed');
      } finally {
        setIsProcessing(false);
      }
    };

    handleAuthCallback();
  }, [navigate, searchParams]);

  // Show error state
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background to-background/80 flex items-center justify-center p-4">
        <div className="w-full max-w-md bg-black/20 backdrop-blur-sm p-8 rounded-lg border border-white/10 text-center">
          <div className="mb-6">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-500/20 flex items-center justify-center">
              <svg className="w-8 h-8 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h1 className="text-xl font-bold text-white mb-2">Authentication Error</h1>
            <p className="text-gray-300 text-sm">{error}</p>
          </div>
          <div className="space-y-3">
            {error.includes('expired') ? (
              <>
                <button
                  onClick={() => navigate('/auth?forgot=true', { replace: true })}
                  className="w-full bg-primary text-white px-4 py-2 rounded-md hover:bg-primary/90 transition-colors"
                >
                  Request New Reset Link
                </button>
                <button
                  onClick={() => navigate('/auth', { replace: true })}
                  className="w-full bg-white/10 text-white px-4 py-2 rounded-md hover:bg-white/20 transition-colors"
                >
                  Go to Login
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => navigate('/auth', { replace: true })}
                  className="w-full bg-primary text-white px-4 py-2 rounded-md hover:bg-primary/90 transition-colors"
                >
                  Go to Login
                </button>
                <button
                  onClick={() => window.location.reload()}
                  className="w-full bg-white/10 text-white px-4 py-2 rounded-md hover:bg-white/20 transition-colors"
                >
                  Try Again
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Show loading state while processing
  if (isProcessing) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background to-background/80 flex items-center justify-center p-4">
        <div className="w-full max-w-md bg-black/20 backdrop-blur-sm p-8 rounded-lg border border-white/10 text-center">
          <LoadingFallback />
          <p className="text-white mt-4">Processing authentication...</p>
        </div>
      </div>
    );
  }

  return null;
};

export default AuthCallback;
